/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import {
  addDays,
  endOfDay,
  endOfYear,
  isSameDay,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfYear,
} from 'date-fns';

const customPresets = [
  {
    label: 'Forward 7 days',
    range: () => ({
      startDate: addDays(startOfDay(new Date()), 0),
      endDate: addDays(endOfDay(new Date()), 6),
    }),
    isSelected(range) {
      const definedRange = this.range();
      return (
        isSameDay(range.startDate, definedRange.startDate) &&
        isSameDay(range.endDate, definedRange.endDate)
      );
    },
  },
  {
    label: 'Month-to-Date',
    range: () => ({
      startDate: startOfMonth(new Date()),
      endDate: endOfDay(new Date()),
    }),
    isSelected(range) {
      const definedRange = this.range();
      return (
        isSameDay(range.startDate, definedRange.startDate) &&
        isSameDay(range.endDate, definedRange.endDate)
      );
    },
  },
  {
    label: 'Year-to-Date',
    range: () => ({
      startDate: startOfYear(new Date()),
      endDate: endOfDay(new Date()),
    }),
    isSelected(range) {
      const definedRange = this.range();
      return (
        isSameDay(range.startDate, definedRange.startDate) &&
        isSameDay(range.endDate, definedRange.endDate)
      );
    },
  },
  {
    label: 'This Year',
    range: () => ({
      startDate: startOfYear(new Date()),
      endDate: endOfYear(new Date()),
    }),
    isSelected(range) {
      const definedRange = this.range();
      return (
        isSameDay(range.startDate, definedRange.startDate) &&
        isSameDay(range.endDate, definedRange.endDate)
      );
    },
  },
];
//
export default customPresets;

/**
 * Generates predefined stay date presets based on booking data boundaries.
 */
export const getStayDatePreset = (lastFullBookingDate, maxStayDate) => {
  if (!lastFullBookingDate || !maxStayDate) return [];
  //
  const lastFullDate = startOfDay(parseISO(lastFullBookingDate));
  const maxStay = startOfDay(parseISO(maxStayDate));
  //
  return [
    {
      label: 'Last Full Booking Day Onwards',
      range: () => ({
        startDate: lastFullDate,
        endDate: maxStay,
      }),
      isSelected(range) {
        const definedRange = this.range();
        return (
          isSameDay(range.startDate, definedRange.startDate) &&
          isSameDay(range.endDate, definedRange.endDate)
        );
      },
    }
  ];
};
/**
 * Generates predefined stay date presets based on last full booking date.
 */
export const getStayDateCurrentPreset = (lastFullBookingDate) => {
  if (!lastFullBookingDate) return [];
  //
  const lastFullDate = startOfDay(parseISO(lastFullBookingDate));
  const next60DayDate = startOfDay(addDays(parseISO(lastFullBookingDate), 60));
  const next90DayDate = startOfDay(addDays(parseISO(lastFullBookingDate), 90));
  const endOfYearDate = startOfDay(endOfYear(new Date()));
  //
  return [
    {
      label: 'Current + 60 Days',
      toolTip: 'Latest full booking day and 60 days following it (inclusive)',
      hasCustomRendering: true,
      range: () => ({
        startDate: lastFullDate,
        endDate: next60DayDate,
      }),
      isSelected(range) {
        const definedRange = this.range();
        return (
          isSameDay(range.startDate, definedRange.startDate) &&
          isSameDay(range.endDate, definedRange.endDate)
        );
      },
    },
    {
      label: 'Current + 90 Days',
      toolTip: 'Latest full booking day and 90 days following it (inclusive)',
      hasCustomRendering: true,
      range: () => ({
        startDate: lastFullDate,
        endDate: next90DayDate,
      }),
      isSelected(range) {
        const definedRange = this.range();
        return (
          isSameDay(range.startDate, definedRange.startDate) &&
          isSameDay(range.endDate, definedRange.endDate)
        );
      },
    },
    {
      label: 'Current to End of Year',
      toolTip: 'Latest full booking day to the end of the present year (inclusive)',
      hasCustomRendering: true,
      range: () => ({
        startDate: lastFullDate,
        endDate: endOfYearDate,
      }),
      isSelected(range) {
        const definedRange = this.range();
        return (
          isSameDay(range.startDate, definedRange.startDate) &&
          isSameDay(range.endDate, definedRange.endDate)
        );
      },
    },
  ];
};
